import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Music, BookOpen, Trash2 } from "lucide-react";
import ImportShowLibraryButton from "@/components/ImportShowLibraryButton";
import { getProductionId } from "@/lib/api";

export default function Scenes() {
  const { user } = useAuth();
  const canEdit = user.role === "org_admin" || user.role === "production_staff";
  const [scenes, setScenes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", act: "1", is_song: false, characters: [], pages: "", notes: "" });
  const [charsRaw, setCharsRaw] = useState("");

  const load = () => api.get("/scenes").then((r) => setScenes(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, characters: charsRaw.split(",").map((s) => s.trim()).filter(Boolean) };
    await api.post("/scenes", payload);
    setOpen(false);
    setForm({ name: "", act: "1", is_song: false, characters: [], pages: "", notes: "" });
    setCharsRaw("");
    toast.success("Scene added");
    load();
  };
  const del = async (id) => { await api.delete(`/scenes/${id}`); load(); };

  const byAct = scenes.reduce((acc, s) => { (acc[s.act] ||= []).push(s); return acc; }, {});

  return (
    <div className="space-y-6 animate-fade-up" data-testid="scenes-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Structure</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Scenes & Songs</h1>
          <p className="mt-1 text-zinc-400 text-sm">Breakdown by act, with cast on each scene.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <ImportShowLibraryButton productionId={getProductionId()} onImported={load} />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-scene-btn" className="bg-gold text-black hover:bg-gold-hover"><Plus className="w-4 h-4 mr-1" />New scene</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">Add scene or song</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input data-testid="scene-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>Act</Label><Input value={form.act} onChange={(e) => setForm({ ...form, act: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                  <div><Label>Pages</Label><Input value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                  <div className="flex items-end gap-2">
                    <div><Label>Song</Label><div className="pt-2"><Switch checked={form.is_song} onCheckedChange={(v) => setForm({ ...form, is_song: v })} /></div></div>
                  </div>
                </div>
                <div><Label>Characters (comma separated)</Label><Input value={charsRaw} onChange={(e) => setCharsRaw(e.target.value)} className="bg-[#0A0A0A] border-white/10" placeholder="Cinderella, The Baker" /></div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              </div>
              <DialogFooter><Button data-testid="save-scene-btn" onClick={save} className="bg-gold text-black hover:bg-gold-hover">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </header>

      {Object.keys(byAct).sort().map((act) => (
        <section key={act}>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3">Act {act}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {byAct[act].map((s) => (
              <div key={s.id} className="rounded-lg border border-white/10 bg-[#121212] p-5">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-md grid place-items-center ${s.is_song ? "bg-gold/10 text-gold" : "bg-white/5 text-zinc-300"}`}>
                    {s.is_song ? <Music className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight truncate">{s.name}</div>
                    <div className="text-xs text-zinc-500">{s.is_song ? "Song" : "Scene"} · Pages {s.pages || "—"}</div>
                  </div>
                  {canEdit && <button data-testid={`del-scene-${s.id}`} onClick={() => del(s.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                </div>
                {s.characters?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {s.characters.map((c) => (
                      <span key={c} className="text-[11px] rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-zinc-300">{c}</span>
                    ))}
                  </div>
                )}
                {s.notes && <div className="mt-3 text-xs text-zinc-500 whitespace-pre-wrap">{s.notes}</div>}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
